<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/vue/24/outline'
import { post } from '../../lib/fetch-api'
import { calculateMD5 } from '../../lib/auth'
import packageJson from '../../package.json';
const appVersion = packageJson.version;

const email = ref('')
const password = ref('')
const error = ref(null)
const sessionNotice = ref(null)
const capsLockOn = ref(false)
const isPending = ref(false)
const router = useRouter()
const publicStats = ref({
  total_public: 0,
  categories: [],
  subcategories: []
})

// ADD THIS FUNCTION HERE
const getCategoryCount = (categoryName) => {
  const category = publicStats.value.categories.find(
    item => item.name === categoryName
  )

  return category ? category.count : 0
}

const statsLoaded = ref(false)

const loadPublicStats = async () => {
  try {
    const response = await fetch('/api/spectfiles/public-stats', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    })

    if (!response.ok) {
      throw new Error(`Statistics request failed: ${response.status}`)
    }

    const result = await response.json()

    if (result.isSuccess) {
      publicStats.value = result.body
      statsLoaded.value = true
    }
  } catch (err) {
    // Statistics are optional. A failure must not block login.
    console.error('Could not load public statistics:', err)
    statsLoaded.value = false
  }
}

onMounted(() => {
  if (sessionStorage.getItem('sessionExpired')) {
    sessionNotice.value =
      'Your session has expired. Please sign in again.'

    sessionStorage.removeItem('sessionExpired')
  }

  loadPublicStats()
})

const updateCapsLockState = event => {
  capsLockOn.value = event.getModifierState?.('CapsLock') ?? false
}

const handleSubmit = async () => {
  error.value = null

  if (!email.value.trim() || !password.value) {
    error.value = 'Enter your email address and password.'
    return
  }

  sessionNotice.value = null
  isPending.value = true

  const user = {
    email: email.value.trim(),
    password: calculateMD5(password.value),
  }

  try {
    const response = await post("auth/login", user)
    if (response.isSuccess) {
      sessionStorage.removeItem('sessionExpired')
      sessionNotice.value = null

      localStorage.setItem("token", JSON.stringify(response.body.token))
      localStorage.setItem("user", JSON.stringify(response.body.user))
      if (response.body.user.type === "admin") {
        router.push("/admin/mainpage")
      } else
        router.push("/user/main-layout")
    } else {
      error.value = response.message || "Login failed"
    }
  } catch (err) {
    error.value = err
  } finally {
    isPending.value = false
  }
}

</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-start bg-blue-50 px-4 pt-12 pb-8 space-y-4 md:pt-16">
    <h2 class="text-xl font-bold text-gray-600 mb-5 text-center break-words">Welcome to the Spectral Library at Stony Brook University, Department of Geosciences.</h2> 
    <p></p><h2 class="text-xl font-normal text-gray-600 mb-10 text-center break-words">This library contains nano-FTIR, conventional FTIR, and Raman spectra of various samples.      
    <p></p>Users can create their own folders and upload comma-separated data files, visalize, and find spectral matches.
    <p></p>Please create an account below to utilize the spectral library. Registered users are able to share their data with other users. 
    </h2> 

    <!-- Public spectral-library statistics -->
    <div
      v-if="statsLoaded"
      class="w-full max-w-4xl px-4"
    >
      <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <!-- FTIR -->
        <div
          class="px-3 py-2 text-center bg-white border border-blue-200 rounded-lg shadow-sm"
        >
          <p class="text-xl font-semibold text-blue-700">
            {{ getCategoryCount('FTIR') }}
          </p>

          <p class="text-xs text-gray-600">
            FTIR spectra
          </p>
        </div>

        <!-- Nano-FTIR -->
        <div
          class="px-3 py-2 text-center bg-white border border-blue-200 rounded-lg shadow-sm"
        >
          <p class="text-xl font-semibold text-blue-700">
            {{ getCategoryCount('Nano-FTIR') }}
          </p>

          <p class="text-xs text-gray-600">
            Nano-FTIR spectra
          </p>
        </div>

        <!-- Raman -->
        <div
          class="px-3 py-2 text-center bg-white border border-blue-200 rounded-lg shadow-sm"
        >
          <p class="text-xl font-semibold text-blue-700">
            {{ getCategoryCount('Raman') }}
          </p>

          <p class="text-xs text-gray-600">
            Raman spectra
          </p>
        </div>

        <!-- Total public spectra -->
        <div
          class="px-3 py-2 text-center bg-white border border-blue-200 rounded-lg shadow-sm"
        >
          <p class="text-xl font-semibold text-blue-700">
            {{ publicStats.total_public }}
          </p>

          <p class="text-xs text-gray-600">
            Total public spectra
          </p>
        </div>
      </div>
    </div>

    <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
      <h2 class="text-3xl font-semibold text-blue-800 mb-6 text-center">Login</h2>

      <div
        v-if="sessionNotice"
        role="status"
        class="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-amber-800"
      >
        {{ sessionNotice }}
      </div>

      <div v-if="error" class="bg-red-100 text-red-600 p-3 rounded mb-4">
        {{ error }}
      </div>
      <form @submit.prevent="handleSubmit" class="space-y-6" novalidate>
        <div>
          <label for="email" class="sr-only">Email address</label>

          <div class="flex items-center overflow-hidden rounded-lg border border-blue-300">
            <span class="px-3">
              <EnvelopeIcon class="h-5 w-5 text-blue-600" />
            </span>

            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              inputmode="email"
              placeholder="Email address"
              class="flex-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <div>
          <label for="password" class="sr-only">Password</label>

          <div class="flex items-center overflow-hidden rounded-lg border border-blue-300">
            <span class="px-3">
              <LockClosedIcon class="h-5 w-5 text-blue-600" />
            </span>

            <input
              id="password"
              v-model="password"
              type="password"
              autocomplete="current-password"
              placeholder="Password"
              class="flex-1 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              @keydown="updateCapsLockState"
              @keyup="updateCapsLockState"
              @blur="capsLockOn = false"
            />
          </div>

          <p
            v-if="capsLockOn"
            role="status"
            class="mt-2 text-left text-xs text-amber-700"
          >
            Caps Lock is on.
          </p>
        </div>
        <button
          type="submit"
          :disabled="isPending"
          class="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isPending ? "Logging in..." : "Login" }}
        </button>
      </form>
      <div class="mt-6 flex justify-between text-sm">
        <RouterLink to="/register" class="text-blue-600 hover:underline">
          Create an account
        </RouterLink>
        <RouterLink to="/forgot-password" class="text-blue-600 hover:underline">
          Forgot Password?
        </RouterLink>
      </div>

      <!-- Add Version at Bottom -->
      <p class="text-xs text-gray-500 mt-4 text-center">Version: {{ appVersion }}</p>
    </div>
    <h2 class="text-l font-light text-gray-600 mb-5 text-center break-words">Contact: mehmet.yesiltas [at] stonybrook.edu</h2> 
  </div>
</template>



<style scoped></style>
